import type { ContentLanguage } from "@skilitics/nautilus-core-designer-data";

import type { EffectBuilder, EffectBuilderContext } from "../../../types/node-designs/triggers/effect-builders";
import { createEffectBuildable } from "../../effects/createEffectBuildable";
import { LocaleEffectEditor } from "./LocaleEffectEditor";
import { LocaleEffectSummary } from "./LocaleEffectSummary";

interface LocaleEffectConfig {
  locale?: string;
  [key: string]: unknown;
}

type Spec = {
  getContentLanguages?: () => ReadonlyArray<ContentLanguage>;
};

interface LocaleEffectBuilderContext extends EffectBuilderContext {
  config: LocaleEffectConfig;
  spec: Spec;
}

export const localeEffectBuilder: EffectBuilder<LocaleEffectBuilderContext> = {
  typeId: "locale" as const,
  build: ({ config: { locale }, spec: { getContentLanguages } }) => {
    if (!locale) return;

    const isLocaleAvailable = getContentLanguages?.().some((lang) => lang.code === locale);

    if (!isLocaleAvailable) return;

    return {
      type: "core-action" as const,
      action: {
        type: "changeActiveLanguage" as const,
        payload: {
          code: locale,
        },
      },
    };
  },
  isValid: ({ locale }, { getContentLanguages }) =>
    !!locale && (getContentLanguages?.() ?? []).some((lang) => lang.code === locale),
  getInitialConfig: () => ({
    locale: undefined,
  }),
  views: {
    summary: LocaleEffectSummary,
    edit: LocaleEffectEditor,
  },
};

export const localeEffectBuildableCreate = (getContentLanguages: Spec["getContentLanguages"]) =>
  createEffectBuildable(
    localeEffectBuilder,
    "change-locale",
    { title: "Change Locale", getContentLanguages },
    { unique: true },
  );
